import { memo } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { useLocation } from "react-router-dom";
import { parse } from "qs";
import ProductCardFromPrice from "app/pages/.shared/ProductCard/ProductCardFromPrice";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import ProductRatingContainer from "app/pages/FicheProduit/components/ProductRating/ProductRatingContainer";
import { CompactHotelCard } from "app/features/product/components/CompactHotelCard/CompactHotelCard";
import { HotelName } from "app/features/product/components/HotelName/HotelName";
import {
	getBoardsDiscountPercentage,
	getBoardsDiscountValue,
	getDiscountBadgeVisibility,
} from "app/utils/discountUtils";
import { BadgeItem } from "app/components/ui/badge/BadgeItem";
import { OFFER_PRICE_TYPES, PRODUCT_TAG_CODES, TRANSPORTATION_TYPES } from "app/constants";
import "./SmartDPMapItem.scss";

const TAG_BADGE_TYPES = {
	[PRODUCT_TAG_CODES.GC_DISCONNECTED]: "membersOnly",
	[PRODUCT_TAG_CODES.LIMITED_TIME]: "limited",
	[PRODUCT_TAG_CODES.LAST_DAY]: "lastDays",
	[PRODUCT_TAG_CODES.COUP_DE_COEUR]: "topPicks",
	[PRODUCT_TAG_CODES.DISCOUNT]: "discount",
	[PRODUCT_TAG_CODES.BAGGAGE_INCLUDED]: "luggage",
};

const SmartDPMapItem = ({ accommodation, tag, quoteLink, onClick, isSpotlight = false }) => {
	const { search } = useLocation();
	const badgeType = TAG_BADGE_TYPES[tag];
	const queryParams = parse(search, { ignoreQueryPrefix: true });
	const showDiscountedAccommodation = getDiscountBadgeVisibility(accommodation);
	const discountValue = getBoardsDiscountValue(accommodation);
	const discountedPercentage = getBoardsDiscountPercentage(accommodation, discountValue);
	const tripAdvisor = accommodation.tripadvisor || {};
	const holidayCheck = accommodation.holidaycheck || {};
	const accommodationNights = Number(accommodation?.accommodationNights || queryParams?.duration);
	const photo = accommodation.photos?.[0];
	const flightDetails = accommodation.flightDetails;

	const price = accommodation.total > 0 && (
		<ProductCardFromPrice
			value={accommodation.total}
			priceSuffix={<FormattedMessage id="general.amount.per.person.suffix.label.short" />}
			size="large"
			suffixSize="xsmall"
			offerPriceType={OFFER_PRICE_TYPES.FROM_PRICE_TYPE_FROM_WITH_FLIGHT}
			includeOfferWithFlight={Boolean(flightDetails)}
			travelByTrainAvailable={flightDetails?.type === TRANSPORTATION_TYPES.TRAIN}
			duration={accommodationNights}
			showFromInfo={accommodationNights > 0}
			priceAddon={
				showDiscountedAccommodation &&
				discountedPercentage !== 0 && (
					<Typography
						className="smartdp-map-item__discount-percentage"
						variant={TYPOGRAPHY_VARIANTS.REGULAR}
						data-testid="smartDPMap-item-discount-percentage"
					>
						-{discountedPercentage}%
					</Typography>
				)
			}
		/>
	);

	return (
		<CompactHotelCard
			to={quoteLink.to}
			state={quoteLink.state}
			onClick={onClick}
			title={<HotelName name={accommodation.name} category={accommodation.category} />}
			photo={photo?.url ? { url: photo.url } : undefined}
			badges={
				badgeType && (
					<BadgeItem type={badgeType} showPrimaryText={false} showSecondaryText={false} />
				)
			}
			rating={
				<ProductRatingContainer
					locationName={accommodation.name}
					hideModal
					tripAdvisor={{
						id: tripAdvisor.id,
						rating: tripAdvisor.rating,
						reviewsCount: tripAdvisor.num_reviews,
						hideIcon: true,
					}}
					holidayCheck={{
						...holidayCheck,
						size: "small",
					}}
				/>
			}
			price={price}
			spotlightLabel={isSpotlight && <FormattedMessage id="sdp.product.spotlight.title" />}
			className="smartdp-map-item"
		/>
	);
};

SmartDPMapItem.propTypes = {
	accommodation: PropTypes.object,
	tag: PropTypes.oneOf(Object.values(PRODUCT_TAG_CODES)),
	quoteLink: PropTypes.shape({
		to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
		state: PropTypes.object,
	}).isRequired,
	onClick: PropTypes.func,
	isSpotlight: PropTypes.bool,
};

export default memo(SmartDPMapItem);
